import styles from './FeaturesOpinions.module.css'
import SectionHeader from './SectionHeader'
import Carousel from './Carousel'
import type { DealHuntOpinion } from 'lib/dealhunt/types'
import { useCatalogEvent } from 'lib/analytics/catalog'

interface FeaturesOpinionsProps {
  opinions: DealHuntOpinion[]
}

/** Two-letter initials for the avatar fallback ("Adam Birney" -> "AB"). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/)
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)
  return letters.toUpperCase()
}

/**
 * "Features & Opinions" carousel — real SoundGuys editorial posts from DealHunt's
 * `enrichment.opinions` (linked to this product/category; newest first). The API has
 * no Feature/Opinion sub-type, so no per-card kind badge is shown. The template only
 * mounts this section when there's at least one post.
 */
const FeaturesOpinions: React.VFC<FeaturesOpinionsProps> = ({ opinions }) => {
  const { track } = useCatalogEvent()
  return (
    <section className={styles.section}>
      <SectionHeader
        title="Features & Opinions"
        link={{
          href: 'https://www.soundguys.com/category/features/',
          label: 'More from the team ›',
        }}
        intro="Commentary and deep dives from the SoundGuys editorial team."
      />

      <Carousel>
        {opinions.map((o, i) => {
          const author = o.authors[0] ?? null
          return (
            <a
              key={o.postId}
              className={styles.card}
              href={o.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                track('product_page_opinion_click', {
                  target_id: o.postId,
                  target_title: o.title,
                  destination_url: o.url,
                  position: i + 1,
                })
              }
            >
              <span className={styles.cardBody}>
                <span className={styles.title}>{o.title}</span>
                {o.subtitle && <span className={styles.excerpt}>{o.subtitle}</span>}
              </span>
              {author && (
                <span className={styles.author}>
                  {author.avatarUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img className={styles.avatar} src={author.avatarUrl} alt={author.name} />
                  ) : (
                    <span className={styles.avatar}>{initialsOf(author.name)}</span>
                  )}
                  <span className={styles.authorText}>
                    <span className={styles.authorName}>{author.name}</span>
                    {author.position && (
                      <span className={styles.authorRole}>{author.position}</span>
                    )}
                  </span>
                </span>
              )}
            </a>
          )
        })}
      </Carousel>
    </section>
  )
}

export default FeaturesOpinions
